import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IntakeSurveySection from '../components/intake/IntakeSurveySection';
import ApiClient from '../config/ApiClient';
import { isQuestionRequired, visibleQuestions, visibleSectionGroups } from '../hooks/useIntakeSurvey';
import HeroSection from '../theme/HeroSection';
import { IntakeAnswers, IntakeSchema, RequestAccessResult } from '../types/intakeSurvey';

// Server field keys mapped back onto the schema question ids for inline errors.
const SERVER_FIELD_TO_QUESTION: Record<string, string> = {
  organization: 'organization',
  org_id: 'organization',
  inviter_contact: 'inviter_contact',
};

const RequestAccessPage: React.FC = () => {
  const [schema, setSchema] = useState<IntakeSchema | null>(null);
  const [answers, setAnswers] = useState<IntakeAnswers>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<RequestAccessResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await ApiClient.get<IntakeSchema>('/api/intake/request_access/schema/');
      if (cancelled) return;
      if (res.success && res.data) {
        setSchema(res.data);
      } else {
        setError(res.error ?? 'Unable to load the request access form.');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleSections = useMemo(() => visibleSectionGroups(schema, answers), [schema, answers]);
  const visibleFields = useMemo(() => visibleQuestions(schema, answers), [schema, answers]);

  const setAnswer = useCallback((id: string, value: string | number | string[]) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setFieldErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const validateAll = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    for (const question of visibleFields) {
      const value = answers[question.id];
      const required = isQuestionRequired(question, answers);
      const empty =
        value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
      if (required && empty) {
        errors[question.id] = 'This field is required.';
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }
    return true;
  }, [answers, visibleFields]);

  const submit = useCallback(async () => {
    if (!validateAll()) {
      setError('Please give us a contact or organization to find your program.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    const res = await ApiClient.post<RequestAccessResult>('/api/request-access/', {
      org_id: answers['organization'],
      inviter_contact: answers['inviter_contact'],
      source_url: window.location.href,
    });

    setSubmitting(false);

    if (res.success && res.data) {
      setResult(res.data);
      return;
    }

    if (res.errors) {
      const mapped: Record<string, string> = {};
      for (const [key, val] of Object.entries(res.errors)) {
        const questionId = SERVER_FIELD_TO_QUESTION[key] ?? key;
        mapped[questionId] = Array.isArray(val) ? val.join(' ') : String(val);
      }
      setFieldErrors(mapped);
      setError('Please give us a contact or organization to find your program.');
      return;
    }
    setError(res.error ?? 'Something went wrong. Please try again.');
  }, [answers, validateAll]);

  const matches = result?.found ? result.matches ?? [] : [];

  return (
    <Box>
      <HeroSection minHeight="30vh" showWave imageUrl="/images/SeanValentine-HGP.jpg">
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
            Find your program
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="sm" sx={{ py: 6 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && result && result.found && matches.length > 0 && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Good news — we found your program. Continue to the right site to pick up where you left off.
            </Alert>
            <Stack spacing={2}>
              {matches.map((match) => (
                <Box
                  key={match.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Avatar src={match.logo ?? undefined} alt={match.name} variant="rounded">
                    {match.name?.[0] ?? '?'}
                  </Avatar>
                  <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{match.name}</Typography>
                  {match.login_url ? (
                    <Button variant="contained" color="secondary" href={match.login_url}>
                      Go to {match.name}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Contact your organization
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {!loading && result && !result.found && (
          <Alert severity="success">
            Thanks — we&apos;ve shared your request with the organization. We&apos;ll contact you when they respond.
          </Alert>
        )}

        {!loading && !result && schema && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            noValidate
          >

            {visibleSections.map((group) => (
              <IntakeSurveySection
                key={group.section.id}
                group={group}
                answers={answers}
                fieldErrors={fieldErrors}
                onChange={setAnswer}
              />
            ))}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button type="submit" variant="contained" disabled={submitting} sx={{ minWidth: 140 }}>
                {submitting ? 'Checking…' : 'Find my program'}
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default RequestAccessPage;
